import { Drawer } from '@components/common/drawer/drawer';
import FilterIcon from '@components/icons/filter-icon';
import { useUI } from '@contexts/ui.context';
import FilterSidebar from '@components/search/filter-sidebar';
import ListBox from '@components/ui/filter-list-box';
import { TopFilters } from '@components/search/top-filters';
import { useTranslation } from '@i18n/client';
import { getDirection } from '@utils/get-direction';
import motionProps from '@components/common/drawer/motion';
import Search from '@components/common/search';
import ProductsGridSearch from '@components/search/products-grid-search';
import { useLocation } from 'react-router-dom';

export default function SearchTopBar({ lang }: { lang: string }) {
  const { openFilter, displayFilter, closeFilter } = useUI();
  const { t } = useTranslation(lang, 'common');
  const dir = getDirection(lang);
  const location = useLocation();
  const contentWrapperCSS = dir === 'ltr' ? { left: 0 } : { right: 0 };

  // Extract the path after the language prefix to check if we're on exact "/products" route
  const pathWithoutLang =
    location.pathname.replace(new RegExp(`^/${lang}`), '') || '/';
  const isExactProductsRoute =
    pathWithoutLang === '/products' || pathWithoutLang === '/products/';
  const SearchComponent = isExactProductsRoute ? ProductsGridSearch : Search;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <SearchComponent
          searchId="mobile-search"
          className="top-bar-search lg:hidden z-30 px-4 md:px-6 top-1"
          lang={lang}
        />
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            className="flex items-center px-4 py-2 text-sm font-semibold transition duration-200 ease-in-out border rounded-md text-brand-dark border-border-base focus:outline-none hover:border-brand hover:text-brand"
            onClick={openFilter}
          >
            <FilterIcon />
            <span className="ltr:pl-2.5 rtl:pr-2.5">
              {t('text-categories')}
            </span>
          </button>
          <ListBox
            options={[
              { name: 'text-category', value: 'category' },
              { name: 'text-alphabet-asc', value: 'name-asc' },
              { name: 'text-alphabet-desc', value: 'name-desc' },
              { name: 'text-kosher', value: 'kosher' },
              { name: 'text-new-arrival', value: 'date-desc' },
            ]}
            lang={lang}
          />
        </div>
        <div className="flex items-center space-x-3 overflow-x-auto pb-2 rtl:space-x-reverse">
          <TopFilters lang={lang} />
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center justify-between mb-6">
        <div className="shrink-0 text-brand-dark font-medium text-15px leading-4 md:ltr:mr-6 md:rtl:ml-6 mt-0.5">
          {/*2,683 {t('text-items-found')}*/}
        </div>
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <TopFilters lang={lang} />
          <ListBox
            options={[
              { name: 'text-category', value: 'category' },
              { name: 'text-alphabet-asc', value: 'name-asc' },
              { name: 'text-alphabet-desc', value: 'name-desc' },
              { name: 'text-kosher', value: 'kosher' },
              { name: 'text-new-arrival', value: 'date-desc' },
            ]}
            lang={lang}
          />
        </div>
      </div>
      {/*TODO: multiple drawer uses throughout the app is a bad practice */}
      <Drawer
        placement={dir === 'rtl' ? 'right' : 'left'}
        open={displayFilter}
        onClose={closeFilter}
        // @ts-ignore
        level={null}
        contentWrapperStyle={contentWrapperCSS}
        {...motionProps}
      >
        <FilterSidebar lang={lang} />
      </Drawer>
    </>
  );
}
